import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Rating,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../components/common/ShowToast";
import apiService from "../../../../service/Api";

interface VenueReview {
  id: number;
  rating: number;
  comment: string;
  status: "pending" | "approved" | "rejected";
  author: string;
  author_email: string;
  public_name: string;
  created_at: string;
}

const BRAND = "#724e41";
const DANGER = "#ef4444";
const RATING_LABELS = ["Poor", "Fair", "Good", "Very good", "Excellent"];

const formatDate = (d: string) =>
  new Date(d).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const plural = (count: number) => `${count} review${count === 1 ? "" : "s"}`;

function ReviewSkeleton() {
  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Skeleton variant="rectangular" width={20} height={20} sx={{ mt: 0.5, borderRadius: 1 }} />
        <Skeleton variant="circular" width={36} height={36} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="30%" height={18} />
          <Skeleton variant="text" width="20%" height={14} sx={{ mt: 0.5 }} />
          <Skeleton variant="text" width="100%" height={14} sx={{ mt: 1.5 }} />
          <Skeleton variant="text" width="80%" height={14} />
        </Box>
      </Stack>
    </Box>
  );
}

/**
 * Website reviews of this venue. They show on the venues website as soon as
 * they're written; staff delete any that are offensive or inappropriate here.
 */
export default function VenueReviewsTab({ venueId }: { venueId: number }) {
  const [reviews, setReviews] = useState<VenueReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  // The reviews awaiting confirmation in the delete dialog.
  const [pendingDelete, setPendingDelete] = useState<number[] | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await apiService.get<{ results: VenueReview[] }>(`/api/venues/venue/${venueId}/reviews/`);
      setReviews(data.results);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleCheck = (id: number) =>
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const askToDeleteSelected = () => {
    if (checkedIds.length === 0) {
      toast.warning("Please select reviews first");
      return;
    }
    setPendingDelete(checkedIds);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await apiService.delete(`/api/venues/venue/${venueId}/reviews/?ids=${pendingDelete.join(",")}`);
      setReviews((prev) => prev.filter((r) => !pendingDelete.includes(r.id)));
      setCheckedIds((prev) => prev.filter((id) => !pendingDelete.includes(id)));
      showSuccessToast(`${plural(pendingDelete.length)} deleted`);
      setPendingDelete(null);
    } catch {
      showErrorToast("Failed to delete the review");
    } finally {
      setDeleting(false);
    }
  };

  const allChecked = reviews.length > 0 && checkedIds.length === reviews.length;

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
            {loading ? "Reviews" : `All reviews (${reviews.length})`}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
            Reviews appear on the venues website as soon as they&apos;re written. Delete any that are offensive or inappropriate.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {reviews.length > 0 && (
            <Button
              size="small"
              onClick={() => setCheckedIds(allChecked ? [] : reviews.map((r) => r.id))}
              sx={{ textTransform: "none", fontWeight: 600, color: BRAND, whiteSpace: "nowrap" }}
            >
              {allChecked ? "Clear selection" : "Select all"}
            </Button>
          )}
          <Button
            variant="contained"
            disabled={checkedIds.length === 0}
            onClick={askToDeleteSelected}
            sx={{
              bgcolor: DANGER,
              color: "white",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "13px",
              height: 38,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#dc2626" },
            }}
          >
            Delete selected{checkedIds.length ? ` (${checkedIds.length})` : ""}
          </Button>
        </Stack>
      </Stack>

      <Paper
        sx={{
          borderRadius: "16px",
          p: { xs: 2, sm: 3 },
          bgcolor: "background.paper",
          boxShadow: 0,
          maxHeight: 560,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "action.hover", borderRadius: 4 },
        }}
      >
        <Stack spacing={0}>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => <ReviewSkeleton key={i} />)
          ) : loadError ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>Reviews could not be loaded.</Typography>
              <Button size="small" onClick={() => void load()} sx={{ mt: 1, color: BRAND, textTransform: "none", fontWeight: 600 }}>
                Try again
              </Button>
            </Box>
          ) : reviews.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>No reviews for this venue yet</Typography>
            </Box>
          ) : (
            reviews.map((review, index) => (
              <ReviewCard
                key={review.id}
                review={review}
                checked={checkedIds.includes(review.id)}
                onCheck={() => toggleCheck(review.id)}
                isLast={index === reviews.length - 1}
                onDelete={() => setPendingDelete([review.id])}
              />
            ))
          )}
        </Stack>
      </Paper>

      <Dialog open={pendingDelete !== null} onClose={() => !deleting && setPendingDelete(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Delete {pendingDelete && pendingDelete.length > 1 ? plural(pendingDelete.length) : "this review"}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "14px" }}>
            {pendingDelete && pendingDelete.length > 1 ? "They are" : "It is"} removed from the venues website straight away.
            This can&apos;t be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={deleting} onClick={() => setPendingDelete(null)} sx={{ textTransform: "none", color: "text.secondary" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={deleting}
            onClick={() => void confirmDelete()}
            sx={{ bgcolor: DANGER, textTransform: "none", fontWeight: 700, "&:hover": { bgcolor: "#dc2626" } }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ReviewCard({
  review,
  checked,
  onCheck,
  isLast,
  onDelete,
}: {
  review: VenueReview;
  checked: boolean;
  onCheck: () => void;
  isLast: boolean;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const TRUNCATE_LENGTH = 200;
  const isLong = review.comment.length > TRUNCATE_LENGTH;
  const shown = !expanded && isLong ? review.comment.slice(0, TRUNCATE_LENGTH) + "..." : review.comment;
  const initials = review.author.split(" ").map((n) => n.charAt(0)).slice(0, 2).join("").toUpperCase();

  return (
    <Box sx={{ py: 1.5, position: "relative" }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Checkbox
          checked={checked}
          onChange={onCheck}
          inputProps={{ "aria-label": `Select review by ${review.author}` }}
          sx={{ p: 0, mt: 0.5, color: "text.secondary", "&.Mui-checked": { color: BRAND } }}
        />
        {!isLast && (
          <Box sx={{ position: "absolute", left: 38, top: 56, bottom: -4, width: "2px", bgcolor: "action.hover" }} />
        )}
        <Avatar sx={{ width: 36, height: 36, bgcolor: BRAND, fontSize: "13px", fontWeight: 700, zIndex: 1 }}>
          {initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "text.primary" }}>{review.author}</Typography>
          <Typography sx={{ fontSize: "12px", color: "text.secondary", wordBreak: "break-all" }}>
            {review.author_email} · shown publicly as {review.public_name}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" rowGap={0.5}>
            <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>{formatDate(review.created_at)}</Typography>
            {review.status === "rejected" && (
              <Chip
                label="Hidden from website"
                size="small"
                sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, fontSize: "11px", height: 20, borderRadius: "4px" }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <Rating value={review.rating} readOnly size="small" sx={{ color: "#f59e0b" }} />
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "text.secondary" }}>
              {RATING_LABELS[review.rating - 1]}
            </Typography>
          </Stack>

          <Typography
            sx={{
              fontSize: "13px",
              color: review.comment ? "text.primary" : "text.secondary",
              lineHeight: 1.6,
              mt: 1,
              mb: 1.5,
              whiteSpace: "pre-line",
            }}
          >
            {review.comment ? shown : "No comment, rating only."}
            {isLong && (
              <Box component="span" onClick={() => setExpanded(!expanded)} sx={{ color: BRAND, fontWeight: 600, cursor: "pointer", ml: 0.5 }}>
                {expanded ? " see less" : "see more"}
              </Box>
            )}
          </Typography>

          <Button
            size="small"
            onClick={onDelete}
            sx={{
              bgcolor: DANGER,
              color: "#fff",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "12px",
              px: 1.5,
              py: 0.5,
              minWidth: "unset",
              "&:hover": { bgcolor: "#dc2626" },
            }}
          >
            Delete
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
